// Null-safe адаптеры для унифицированного контракта списков/одиночных ответов
import type { ListResult, SingleResult, SourceTag } from './types';

export function asList<T = any>(response: any): ListResult<T> {
  // ✅ NUCLEAR SAFETY: Outer try-catch to prevent ANY crash
  try {
    if (!response || response.ok === false) {
      return {
        ok: false,
        error: response?.error,
        disabled: response?.disabled || false,
        source: (response?.source || 'rpc-error') as SourceTag
      };
    }

    // Форматы: {data:[...]}, {data:{data:[],total,has_more}}, прямой массив, BFF-формат unified
    // Также поддерживаем JSONB ответы: {data: {data: [...], total: N, has_more: bool}}
    // Edge Function формат: {data: {items: [...], total: N}}
    // CRITICAL: normalizeEdgePayload может вернуть {items: [...], total: N} напрямую
    const data = response?.data;

    const items: T[] =
      // 1. Nested format: {data: {data: [...]}}
      Array.isArray(data?.data) ? (data.data as T[]) :
        // 2. Edge Function format: {data: {items: [...], total: N}}
        Array.isArray(data?.items) ? (data.items as T[]) :
          // 3. Direct array in data: {data: [...]}
          Array.isArray(data) ? (data as T[]) :
            // 4. Response is direct array
            Array.isArray(response) ? (response as T[]) :
              // 5. Response has items at root: {items: [...]}
              Array.isArray(response?.items) ? (response.items as T[]) :
                // 6. Fallback: empty array
                [];

    // ✅ NULL-SAFE: Type guards and NaN checks (fixes ReferenceError in production)
    let total = items.length; // Fallback
    try {
      total =
        (typeof response?.total === 'number' && !isNaN(response.total)) ? response.total :
          (typeof data?.total === 'number' && !isNaN(data.total)) ? data.total : // Edge Function format: {items: [...], total: N}
            items.length;
    } catch { total = items.length; }

    let limit: number | undefined = undefined;
    try {
      limit =
        (typeof response?.limit === 'number' && response.limit > 0) ? response.limit :
          (typeof data?.limit === 'number' && data.limit > 0) ? data.limit :
            undefined;
    } catch { limit = undefined; }

    let offset = 0;
    try {
      offset =
        (typeof response?.offset === 'number' && response.offset >= 0) ? response.offset :
          (typeof data?.offset === 'number' && data.offset >= 0) ? data.offset :
            0;
    } catch { offset = 0; }

    // ✅ NULL-SAFE: Try-catch for math operations to prevent minified code crashes
    // Поддерживаем как has_more (snake_case) так и hasMore (camelCase)
    let has_more = false;
    try {
      has_more = Boolean(
        response?.has_more ??
        response?.hasMore ??
        data?.has_more ??
        data?.hasMore ??
        (typeof limit === 'number' && typeof offset === 'number' && typeof total === 'number'
          ? (offset + limit) < total
          : false)
      );
    } catch (error) {
      console.error('[asList] has_more calculation failed:', error);
      has_more = false;
    }

    return {
      ok: true,
      data: items,
      total,
      limit,
      offset,
      has_more,
      disabled: false as const,
      source: (response?.source || 'success') as SourceTag
    };
  } catch (error) {
    // ✅ NUCLEAR RECOVERY: Return safe empty list on ANY error
    console.error('[asList] NUCLEAR CATCH - returning empty list:', error);
    return {
      ok: true,
      data: [] as T[],
      total: 0,
      limit: undefined,
      offset: 0,
      has_more: false,
      disabled: false as const,
      source: 'nuclear-recovery' as SourceTag
    };
  }
}

export function asSingle<T = any>(response: any): SingleResult<T> {
  if (!response || response.ok === false) {
    const result: any = {
      ok: false,
      error: response?.error,
      disabled: response?.disabled || false,
      source: (response?.source || 'rpc-error') as SourceTag
    };
    if (response?.code) {
      result.code = response.code;
    }
    if (response?.traceId) {
      result.traceId = response.traceId;
    }
    return result;
  }

  let data = response?.data ?? response?.item ?? null;

  if (
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    'data' in (data as Record<string, unknown>)
  ) {
    const container = data as { data: unknown; _meta?: unknown };
    const inner = container.data ?? null;

    if (container._meta !== undefined && inner && typeof inner === 'object' && !Array.isArray(inner)) {
      data = { ...(inner as Record<string, unknown>), _meta: container._meta } as unknown as T;
    } else {
      data = inner as T;
    }
  }
  const success: any = {
    ok: true,
    data: (data as T) ?? null,
    disabled: false as const,
    source: (response?.source || 'success') as SourceTag
  };
  if (response?.traceId) {
    success.traceId = response.traceId;
  }
  if (response?.code) {
    success.code = response.code;
  }
  return success;
}

export function safeLength(arr: unknown): number { return Array.isArray(arr) ? arr.length : 0; }
export function safeArray<T = any>(arr: unknown, fallback: T[] = []): T[] { return Array.isArray(arr) ? (arr as T[]) : fallback; }

export function debugRpcResponse(label: string, resp: any) {
  if (import.meta?.env?.DEV) {

    // console.log(`[RPC Debug] ${label}`, { ok: resp?.ok, keys: resp && typeof resp === 'object' ? Object.keys(resp) : null, isArray: Array.isArray(resp), dataIsArray: Array.isArray(resp?.data) });
  }
  return resp;
}
